import { useState, useEffect } from 'react';
import { Copy, Share2, Gift, Users, DollarSign, TrendingUp, CheckCircle, Link, Award, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getReferralStats } from '../services/api';

const TIERS = [
  { referrals: 1,  reward: '$15 credit',  milestone: 'First referral',     icon: Gift,       color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/20' },
  { referrals: 5,  reward: '$100 credit', milestone: '5 active referrals', icon: Award,      color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20' },
  { referrals: 10, reward: '1 month free',milestone: '10 referrals',       icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  { referrals: 25, reward: 'Gold upgrade', milestone: '25 referrals',      icon: Users,      color: 'text-purple-400',  bg: 'bg-purple-500/10 border-purple-500/20' },
];

function ShareCard() {
  return (
    <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 via-[#0d0e14] to-gray-900 border border-amber-400/20 p-6 max-w-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg bg-amber-400 flex items-center justify-center">
          <TrendingUp className="w-4 h-4 text-gray-900" />
        </div>
        <span className="font-bold text-white text-sm">haythix</span>
      </div>
      <p className="text-xs text-gray-400 mb-1">Automated crypto trading</p>
      <p className="text-3xl font-bold text-emerald-400 mb-4">AI-Powered <span className="text-lg">Trading Bots</span></p>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <p className="text-base font-bold text-white">15+</p>
          <p className="text-[10px] text-gray-500">Strategies</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <p className="text-base font-bold text-white">5 Chains</p>
          <p className="text-[10px] text-gray-500">Multi-Chain</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 text-center">
        Start automating at <span className="text-amber-400">haythix.com</span>
      </p>
    </div>
  );
}

export default function ReferralPage() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [cardCopied, setCardCopied] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    getReferralStats()
      .then(data => setStats(data))
      .catch(err => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const refCode = stats?.referralCode || `MT-${(user?.userId || 'DEMO').slice(-6).toUpperCase()}`;
  const refLink = stats?.referralLink || `https://haythix.com/register?ref=${refCode}`;
  const totalEarned = stats?.totalEarned ?? 0;
  const activeCount = stats?.activeCount ?? 0;
  const referrals = stats?.referrals ?? [];

  function copyLink() {
    navigator.clipboard.writeText(refLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  function copyCard() {
    navigator.clipboard.writeText(refLink).catch(() => {});
    setCardCopied(true);
    setTimeout(() => setCardCopied(false), 2500);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-accent-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Referral Program</h1>
        <p className="text-sm text-gray-400 mt-1">Invite traders. Earn credits. Unlock upgrades.</p>
        {fetchError && (
          <p className="text-xs text-red-400 mt-1">Could not load referral data: {fetchError}</p>
        )}
      </div>

      {/* stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Users,      label: 'Active Referrals', value: activeCount,                   color: 'text-blue-400' },
          { icon: DollarSign, label: 'Total Earned',      value: `$${totalEarned.toFixed(2)}`, color: 'text-emerald-400' },
          { icon: Gift,       label: 'Total Referrals',   value: stats?.totalCount ?? 0,       color: 'text-amber-400' },
          { icon: TrendingUp, label: 'Tier Progress',     value: `${activeCount}/5`,            color: 'text-purple-400' },
        ].map(s => (
          <div key={s.label} className="bg-dark-800 border border-dark-700/50 rounded-2xl p-5">
            <s.icon className={`w-5 h-5 ${s.color} mb-3`} />
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* referral link */}
        <div className="space-y-5">
          <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-6">
            <h2 className="font-semibold text-white mb-4">Your Referral Link</h2>
            <div className="flex gap-2 mb-4">
              <div className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-xs text-gray-300 font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                {refLink}
              </div>
              <button
                onClick={copyLink}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium transition-all ${
                  copied
                    ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                    : 'bg-accent-primary/10 border border-accent-primary/20 text-accent-primary hover:bg-accent-primary hover:text-dark-900'
                }`}
              >
                {copied ? <><CheckCircle className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            <div className="flex items-center gap-2 p-3 bg-dark-700/50 rounded-xl mb-4">
              <Link className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-400">Referral code: <span className="text-white font-bold">{refCode}</span></span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`https://x.com/intent/tweet?text=${encodeURIComponent(`I'm automating my crypto trading with haythix — AI signals, multi-chain bots, strategy marketplace. Try free: ${refLink}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1d9bf0]/10 border border-[#1d9bf0]/20 text-[#1d9bf0] text-xs font-medium hover:bg-[#1d9bf0]/20 transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" /> Share on X
              </a>
              <button
                onClick={copyCard}
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-dark-700 border border-dark-600 text-gray-300 text-xs font-medium hover:border-dark-500 transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" />
                {cardCopied ? 'Copied!' : 'Copy Card Link'}
              </button>
            </div>
          </div>

          {/* how it works */}
          <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-6">
            <h2 className="font-semibold text-white mb-4">How it works</h2>
            <div className="space-y-3">
              {[
                { step: '1', text: 'Share your unique link with other traders' },
                { step: '2', text: 'They sign up and subscribe to any paid plan' },
                { step: '3', text: `You earn ${data?.terms?.ratePct ?? 30}% of every paid month — Trial users earn $0` },
                { step: '4', text: `Commissions run for the first ${data?.terms?.capMonths ?? 12} months per referee, then stop` },
                { step: '5', text: 'If your referee cancels, your commission ends — earnings already paid are kept' },
              ].map(s => (
                <div key={s.step} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-primary/20 border border-accent-primary/30 flex items-center justify-center text-xs font-bold text-accent-primary flex-shrink-0">
                    {s.step}
                  </div>
                  <p className="text-sm text-gray-300">{s.text}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
              Example: refer one Elite user on monthly billing → you earn <span className="text-emerald-400 font-semibold">$60/month</span> for up to 12 months ({data?.terms?.ratePct ?? 30}% of $199.99) = <span className="text-emerald-400 font-semibold">$720 total</span>.
            </p>
          </div>
        </div>

        {/* right column */}
        <div className="space-y-5">
          {/* milestone tiers */}
          <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-6">
            <h2 className="font-semibold text-white mb-4">Milestone Rewards</h2>
            <div className="space-y-3">
              {TIERS.map(t => {
                const unlocked = activeCount >= t.referrals;
                return (
                  <div
                    key={t.referrals}
                    className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                      unlocked ? t.bg : 'bg-dark-700/30 border-dark-600/50 opacity-60'
                    }`}
                  >
                    <t.icon className={`w-5 h-5 flex-shrink-0 ${unlocked ? t.color : 'text-gray-600'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{t.milestone}</p>
                      <p className="text-xs text-gray-500">{t.reward}</p>
                    </div>
                    {unlocked
                      ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      : <span className="text-xs text-gray-500 flex-shrink-0">{t.referrals - activeCount} more</span>
                    }
                  </div>
                );
              })}
            </div>
          </div>

          {/* referred users */}
          <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-6">
            <h2 className="font-semibold text-white mb-4">Your Referrals</h2>
            {referrals.length === 0 ? (
              <div className="text-center py-8">
                <Gift className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No referrals yet</p>
                <p className="text-xs text-gray-600 mt-1">Share your link to start earning</p>
              </div>
            ) : (
              <div className="space-y-2">
                {referrals.map(r => {
                  // 12-month commission window: show "8 / 12" + a progress bar so
                  // the referrer can see how close each referee is to the cap.
                  const cap = data?.terms?.capMonths ?? 12;
                  const billed = r.billedMonths ?? 0;
                  const pct = Math.min(100, Math.round((billed / cap) * 100));
                  return (
                    <div key={r.email} className="py-2 border-b border-dark-700/50 last:border-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-white">{r.name}</p>
                          <p className="text-xs text-gray-500">{r.date} · {r.plan}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${r.earned > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>
                            {r.earned > 0 ? `+$${r.earned.toFixed(2)}` : '—'}
                          </p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            r.status === 'active'
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : 'bg-amber-500/15 text-amber-400'
                          }`}>{r.status}</span>
                        </div>
                      </div>
                      {r.billedMonths > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                            <span>Month {billed} of {cap}</span>
                            <span>{r.stillAccruing ? `${r.monthsRemaining} mo left` : 'commission ended'}</span>
                          </div>
                          <div className="h-1 bg-dark-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${r.stillAccruing ? 'bg-emerald-500' : 'bg-gray-600'}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* shareable performance card */}
          <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-6">
            <h2 className="font-semibold text-white mb-4">Shareable Performance Card</h2>
            <ShareCard />
          </div>
        </div>
      </div>
    </div>
  );
}
